import { Modal, SubmitButton, Tooltip } from "@xola/ui-kit";
import { uniqBy } from "lodash";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { PurchaseRuleLink, PurchaseRuleLinkEntityObject } from "types";
import { api } from "../../../api/api";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { Response } from "../../../types/Response";
import { NoTranslate } from "../../Common/NoTranslate";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { TermsTemplateTable } from "./TermsTemplateTable";

interface ApplyTermsTemplateModalProps {
    isOpen: boolean;
    selectedTermsTemplate?: PurchaseRuleLink;
    onClose: () => void;
    agents: ID[];
}

export const ApplyTermsTemplateModal = ({
    isOpen,
    onClose,
    agents,
    selectedTermsTemplate,
}: ApplyTermsTemplateModalProps) => {
    return (
        <Modal size="huge" isOpen={isOpen} onClose={onClose}>
            <ModalContent agents={agents} selectedTermsTemplate={selectedTermsTemplate} onClose={onClose} />
        </Modal>
    );
};

interface ModalContentProps {
    onClose: () => void;
    selectedTermsTemplate?: PurchaseRuleLink;
    agents: ID[];
}

const ModalContent = ({ onClose, agents, selectedTermsTemplate }: ModalContentProps) => {
    const [selectedTemplate, setSelectedTemplate] = useState<PurchaseRuleLink | null>(null);

    const { mutateAsync: updateTermsTemplate, isLoading, isSuccess } = useUpdateTermsTemplate();

    if (isSuccess) {
        onClose();
    }

    return (
        <>
            <Modal.Header>Commercial Terms Templates</Modal.Header>

            <Modal.Body className="max-h-100 overflow-auto">
                <TermsTemplateTable
                    showActions={false}
                    selectedTermsTemplate={selectedTermsTemplate}
                    components={{
                        Name: ({ termsTemplate }) => (
                            <label className="flex w-40 items-start space-x-2">
                                <input
                                    disabled={isLoading}
                                    checked={selectedTemplate?.id === termsTemplate.id}
                                    type="radio"
                                    className="mt-1"
                                    onChange={() => {
                                        setSelectedTemplate(termsTemplate);
                                    }}
                                />

                                <Tooltip content={termsTemplate.name ?? "-"}>
                                    <NoTranslate>
                                        <div className="flex-1 overflow-hidden">
                                            <div className="text-ellipsis break-words break-all line-clamp-2">
                                                {termsTemplate.name ?? "-"}
                                            </div>
                                        </div>
                                    </NoTranslate>
                                </Tooltip>
                            </label>
                        ),
                    }}
                />
            </Modal.Body>

            <Modal.Footer>
                <SecondaryButton disabled={isLoading} onClick={onClose}>
                    Cancel
                </SecondaryButton>

                <SubmitButton
                    disabled={!selectedTemplate}
                    isLoading={isLoading}
                    onClick={async () => updateTermsTemplate({ agentIds: agents, selectedTemplate })}
                >
                    Apply Template
                </SubmitButton>
            </Modal.Footer>
        </>
    );
};

interface useUpdateTermsTemplateProps {
    agentIds: ID[];
    selectedTemplate: PurchaseRuleLink | null;
}

export const useUpdateTermsTemplate = () => {
    const invalidate = useInvalidate();

    return useMutation(async ({ agentIds, selectedTemplate }: useUpdateTermsTemplateProps) => {
        // Unassign agent from other term templates
        for (const agentId of agentIds) {
            // Find all term templates assigned to this agent
            const { data } = await api.get<Response<PurchaseRuleLink>>("/purchaseRuleLinks", {
                params: { "entities.id": agentId, "tags._id": "Affiliate" },
            });

            for (const purchaseRuleLink of data.data) {
                // Update terms template and remove the agent
                await api.put(`/purchaseRuleLinks/${purchaseRuleLink.id}`, {
                    entities: purchaseRuleLink.entities.filter(({ id }) => id !== agentId),
                });
            }
        }

        const entities = [
            ...selectedTemplate!.entities,
            ...agentIds.map((agentId) => ({ id: agentId, object: PurchaseRuleLinkEntityObject.AFFILIATE })),
        ];

        await api.put(`/purchaseRuleLinks/${selectedTemplate!.id}`, {
            entities: uniqBy(entities, (entity) => entity.id),
        });

        invalidate("purchaseRuleLinks", selectedTemplate!.id);
    });
};
